import React, { useState, useEffect, useCallback } from "react";
import config from "../config";
import { devLog } from "../utils/devLog";

export default function AdminLeagueManagement({ adminToken }) {
  const [leagues, setLeagues] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Fetch all leagues and users
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      devLog("Fetching admin data (authenticated):", Boolean(adminToken));
      devLog("API URL:", config.API_URL);

      // Fetch leagues
      devLog("Attempting to fetch leagues...");
      const leaguesRes = await fetch(`${config.API_URL}/admin/leagues`, {
        headers: { 
          "token": adminToken,
          "Content-Type": "application/json"
        }
      });
      devLog("Leagues response status:", leaguesRes.status);
      if (!leaguesRes.ok) {
        const errorText = await leaguesRes.text();
        console.error("Leagues error:", errorText);
        throw new Error(`Failed to fetch leagues: ${leaguesRes.status} ${errorText}`);
      }
      const leaguesData = await leaguesRes.json();
      devLog("Leagues data:", leaguesData);
      setLeagues(leaguesData);

      // Fetch users
      devLog("Attempting to fetch users...");
      const usersRes = await fetch(`${config.API_URL}/admin/users`, {
        headers: { 
          "token": adminToken,
          "Content-Type": "application/json"
        }
      });
      devLog("Users response status:", usersRes.status);
      if (!usersRes.ok) {
        const errorText = await usersRes.text();
        console.error("Users error:", errorText);
        throw new Error(`Failed to fetch users: ${usersRes.status} ${errorText}`);
      }
      const usersData = await usersRes.json();
      devLog("Users data:", usersData);
      setUsers(usersData);
    } catch (err) {
      console.error("Admin data fetch error:", err);
      setError(`Could not load data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [adminToken]);

  useEffect(() => {
    devLog("AdminLeagueManagement useEffect - token present:", Boolean(adminToken));
    if (adminToken) {
      devLog("Admin token available, fetching data...");
      fetchData();
    } else {
      devLog("No admin token available");
      setError("No admin token available");
    }
  }, [adminToken, fetchData]);

  // Add user to league
  const handleAddUserToLeague = async (leagueId, userId) => {
    setError("");
    setSuccess("");
    try {
      const res = await fetch(`${config.API_URL}/admin/leagues/${leagueId}/add-user/${userId}`, {
        method: "POST",
        headers: { 
          "token": adminToken,
          "Content-Type": "application/json"
        }
      });
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || "Failed to add user to league");
      }
      const data = await res.json();
      setSuccess(data.message);
      // Refresh data to show updated member counts
      setTimeout(() => fetchData(), 1000);
    } catch (err) {
      setError(err.message);
    }
  };



  // Get member count for a league
  const getMemberCount = (league) => {
    return league.members || 0;
  };

  const getLeagueStatusBadge = (league) => {
    const status = (league.payment_status || "").toLowerCase();
    if (status === "refunded") {
      return {
        label: "Refunded - Cancelled",
        background: "#fee2e2",
        color: "#991b1b",
      };
    }

    if (status === "paid") {
      return {
        label: "Paid",
        background: "#dcfce7",
        color: "#166534",
      };
    }

    return {
      label: "Unpaid",
      background: "#f3f4f6",
      color: "#374151",
    };
  };

  // Get verified users only
  const verifiedUsers = users.filter(user => user.is_verified);



  return (
    <div style={{ maxWidth: 1200, margin: "40px auto", padding: 32, background: "#fff", borderRadius: 12, boxShadow: "0 2px 12px #0001" }}>
      <h2 style={{ color: "#2563eb" }}>League Management</h2>
      
      {loading ? (
        <div>Loading data...</div>
      ) : (
        <>
          {error && <div style={{ color: 'red', marginBottom: 8, padding: 8, background: '#fef2f2', borderRadius: 6 }}>{error}</div>}
          {success && <div style={{ color: 'green', marginBottom: 8, padding: 8, background: '#f0fdf4', borderRadius: 6 }}>{success}</div>}
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
            {/* Leagues Section */}
            <div>
              <h3 style={{ color: "#374151", marginBottom: 16 }}>Leagues</h3>
              <div style={{ maxHeight: "500px", overflowY: "auto", border: "1px solid #e0e7ff", borderRadius: 6 }}>
                {leagues.map(league => (
                  <div key={league.id} style={{ padding: 16, borderBottom: "1px solid #f1f5f9" }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <h4 style={{ margin: 0, color: "#1f2937" }}>{league.name}</h4>
                      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <span style={{
                          fontSize: "12px",
                          padding: "2px 8px",
                          borderRadius: 12,
                          background: getLeagueStatusBadge(league).background,
                          color: getLeagueStatusBadge(league).color,
                          fontWeight: 600,
                        }}>
                          {getLeagueStatusBadge(league).label}
                        </span>
                        <span style={{ 
                          fontSize: '12px', 
                          padding: '2px 8px', 
                          borderRadius: 12, 
                          background: league.is_public ? '#dbeafe' : '#fef3c7',
                          color: league.is_public ? '#1e40af' : '#92400e'
                        }}>
                          {league.is_public ? 'Public' : 'Private'}
                        </span>
                      </div>
                    </div>
                    <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: 8 }}>
                      <div>ID: {league.id}</div>
                      <div>Teams: {getMemberCount(league)}/{league.team_count}</div>
                      <div>Type: {league.draft_type} | Scoring: {league.scoring_type}</div>
                    </div>
                                         <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                       League ID: {league.id}
                     </div>
                  </div>
                ))}
                {leagues.length === 0 && (
                  <div style={{ padding: "20px", textAlign: "center", color: '#64748b' }}>
                    No leagues found.
                  </div>
                )}
              </div>
            </div>

            {/* Users Section */}
            <div>
              <h3 style={{ color: "#374151", marginBottom: 16 }}>Verified Users</h3>
              <div style={{ maxHeight: "500px", overflowY: "auto", border: "1px solid #e0e7ff", borderRadius: 6 }}>
                {verifiedUsers.map(user => (
                  <div key={user.id} style={{ padding: 16, borderBottom: "1px solid #f1f5f9" }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <h4 style={{ margin: 0, color: "#1f2937" }}>{user.username}</h4>
                      <span style={{ fontSize: '12px', color: '#6b7280' }}>ID: {user.id}</span>
                    </div>
                    <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: 8 }}>
                      {user.email}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {leagues.map(league => (
                        <button
                          key={league.id}
                          onClick={() => handleAddUserToLeague(league.id, user.id)}
                          className="ffb-btn"
                          style={{
                            fontSize: '11px',
                            padding: '4px 8px',
                            background: getMemberCount(league) >= league.team_count ? '#f3f4f6' : '#2563eb',
                            color: getMemberCount(league) >= league.team_count ? '#9ca3af' : 'white',
                            cursor: getMemberCount(league) >= league.team_count ? 'not-allowed' : 'pointer',
                            border: 'none',
                            borderRadius: 4
                          }}
                          disabled={getMemberCount(league) >= league.team_count}
                          title={getMemberCount(league) >= league.team_count ? 'League is full' : `Add to ${league.name}`}
                        >
                          {league.name} ({getMemberCount(league)}/{league.team_count})
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
                {verifiedUsers.length === 0 && (
                  <div style={{ padding: "20px", textAlign: "center", color: '#64748b' }}>
                    No verified users found.
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div style={{ marginTop: 16, color: '#64748b', fontSize: '14px' }}>
            Total leagues: {leagues.length} | Total verified users: {verifiedUsers.length}
          </div>
        </>
      )}
    </div>
  );
} 